import type { AsyncKeyValueStorage } from "@/lib/telegram";
import { THEME_PREFERENCE_KEY } from "@/lib/storageKeys";

/**
 * design_v2: two states of one construction, switched by **device local
 * time** — `sunset` from 18:00 to 05:59, `dawn` from 06:00 to 17:59
 * (`docs/design/handoff/design/tokens/colors.css`). These are the only two
 * values `data-theme` ever carries; the retired `dark`/`light` pair means
 * nothing to the v2 token layer, where sunset is the bare `:root` block and
 * dawn is `[data-theme="dawn"]`.
 */
export type Theme = "sunset" | "dawn";

/**
 * What the user chose. Automatic-by-clock stays the default; the override
 * is client-local in E0 (stored through `Platform.storage`) — the
 * account-level column and its API land with the Settings screen in E5.
 */
export type ThemePreference = "auto" | Theme;

export const THEME_PREFERENCES: readonly ThemePreference[] = ["auto", "sunset", "dawn"];

/** Local hour at which `dawn` begins. */
export const DAWN_HOUR = 6;
/** Local hour at which `sunset` begins. */
export const SUNSET_HOUR = 18;

/**
 * `--d-ground` per theme, copied from
 * `docs/design/handoff/design/tokens/colors.css` and pinned to it by
 * `__tests__/theme.test.ts`.
 *
 * Duplicated as literals rather than read from the cascade because the two
 * consumers are native APIs that take a hex string, not a CSS value:
 * Telegram's `setHeaderColor`/`setBackgroundColor` and — indirectly — the
 * pre-React paint in `index.html`. Nothing in JSX may read these; components
 * use `var(--d-ground)`.
 */
export const THEME_GROUND: Record<Theme, `#${string}`> = {
  sunset: "#121111",
  dawn: "#EAE2DF",
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** The theme a given moment implies, unless the user has pinned one. */
export function resolveTheme(now: Date, preference: ThemePreference = "auto"): Theme {
  if (preference !== "auto") return preference;
  const hour = now.getHours();
  return hour >= DAWN_HOUR && hour < SUNSET_HOUR ? "dawn" : "sunset";
}

/**
 * Milliseconds from `now` to the next 06:00/18:00 local.
 *
 * Built by mutating a local-midnight-anchored Date rather than by adding a
 * fixed offset, so a DST shift in between still lands on the wall-clock
 * boundary. Clamped to at least a second: a zero/negative delta (a clock
 * jumped backwards onto the boundary) would otherwise spin the timer.
 */
export function msUntilNextBoundary(now: Date): number {
  const hour = now.getHours();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), DAWN_HOUR, 0, 0, 0);
  if (hour >= DAWN_HOUR) next.setHours(SUNSET_HOUR);
  if (hour >= SUNSET_HOUR) {
    next.setDate(next.getDate() + 1);
    next.setHours(DAWN_HOUR);
  }
  return Math.max(1000, next.getTime() - now.getTime());
}

/**
 * Platform chrome that has to be repainted alongside the page — the
 * Telegram header/background and the iOS status bar. Implemented by every
 * `Platform` adapter (`lib/platform/types.ts`); the browser one no-ops.
 */
export interface AppearanceApi {
  /** Called with every theme the app settles on, including the first. */
  apply(theme: Theme): void;
}

export interface ThemeRuntimeOptions {
  /** Element carrying `data-theme`. Defaults to `<html>`. */
  root?: HTMLElement;
  /** Platform chrome. Omit where there is none (tests, previews). */
  appearance?: AppearanceApi | null;
  /** Where the preference lives — `Platform.storage`, never `localStorage`
   *  directly. Omit for an ephemeral runtime. */
  storage?: AsyncKeyValueStorage | null;
  /** Injected clock, so the boundary logic is testable without globals. */
  now?: () => Date;
}

export interface ThemeRuntime {
  theme(): Theme;
  preference(): ThemePreference;
  /** Apply and persist a new preference. */
  setPreference(preference: ThemePreference): void;
  /** Re-resolve against the clock right now. */
  refresh(): void;
  /** Drop the boundary timer and the visibility listener. */
  stop(): void;
}

/**
 * Own `data-theme` for the lifetime of the app.
 *
 * Three things keep it honest, because the init paths run once and the mini
 * app is routinely left open for hours:
 *
 *   1. an immediate apply, before React mounts;
 *   2. a timer armed for the next 06:00/18:00, re-armed after each flip, so
 *      an app open across the boundary follows it;
 *   3. a `visibilitychange` re-check — a backgrounded WKWebView's timers are
 *      throttled or suspended outright, so a phone asleep through 06:00
 *      would otherwise wake up in last night's theme.
 *
 * The stored preference is hydrated asynchronously: storage is async on iOS
 * (Capacitor `Preferences`) and blocking the first paint on it would trade a
 * theme flash for a white one. A pinned preference therefore applies one
 * tick after the clock-resolved default.
 */
export function startThemeRuntime(options: ThemeRuntimeOptions = {}): ThemeRuntime {
  const root = options.root ?? document.documentElement;
  const appearance = options.appearance ?? null;
  const storage = options.storage ?? null;
  const now = options.now ?? ((): Date => new Date());

  let preference: ThemePreference = "auto";
  let current: Theme | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  /** Armed only while the clock decides — a pinned preference has no
   *  boundary to follow. */
  function arm(): void {
    clearTimer();
    if (stopped || preference !== "auto") return;
    timer = setTimeout(() => {
      timer = null;
      apply();
      arm();
    }, msUntilNextBoundary(now()));
  }

  function apply(): void {
    const next = resolveTheme(now(), preference);
    if (next === current) return;
    current = next;
    root.setAttribute("data-theme", next);
    appearance?.apply(next);
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === "hidden") return;
    apply();
    arm();
  }

  apply();
  arm();
  document.addEventListener("visibilitychange", onVisibilityChange);

  if (storage) {
    void storage
      .getItem(THEME_PREFERENCE_KEY)
      .then((stored) => {
        if (stopped || !isThemePreference(stored) || stored === preference) return;
        preference = stored;
        apply();
        arm();
      })
      .catch(() => {
        /* storage unavailable — automatic-by-clock is a fine floor */
      });
  }

  return {
    theme: () => current ?? resolveTheme(now(), preference),
    preference: () => preference,
    setPreference(next: ThemePreference): void {
      preference = next;
      apply();
      arm();
      void storage?.setItem(THEME_PREFERENCE_KEY, next).catch(() => {
        /* the choice still holds for this session */
      });
    },
    refresh(): void {
      apply();
      arm();
    },
    stop(): void {
      stopped = true;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    },
  };
}
